import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  PhoneIcon,
  MailIcon,
  GlobeAltIcon,
  PencilIcon,
  CheckCircleIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/outline";
import {
  updateContactInfoThunk,
  updateSocialMediaThunk,
} from "../../../../store/thunks/branchDisplayThunks";
import Dropdown from "../../../common/Dropdown";

const SOCIAL_PLATFORMS = [
  { _id: "FACEBOOK", name: "Facebook" },
  { _id: "INSTAGRAM", name: "Instagram" },
  { _id: "TWITTER", name: "Twitter" },
  { _id: "YOUTUBE", name: "YouTube" },
  { _id: "LINKEDIN", name: "LinkedIn" },
  { _id: "PINTEREST", name: "Pinterest" },
  { _id: "WHATSAPP_BUSINESS", name: "WhatsApp Business" },
];

const ContactInfoSection = ({ branchCode, isRootAdmin }) => {
  const dispatch = useDispatch();
  const { contactInfo = {}, loadingSection } = useSelector(
    (state) => state.branchDisplay
  );

  const [isEditingContact, setIsEditingContact] = useState(false);
  const [isEditingSocial, setIsEditingSocial] = useState(false);

  // Helper to safely get string value (handles empty objects)
  const safeString = (value) => {
    if (value && typeof value === "object") return "";
    return value || "";
  };

  const [contactForm, setContactForm] = useState({
    phone: {
      primary: "",
      secondary: "",
      sales: "",
      tollfree: "",
      landline: "",
    },
    whatsapp: {
      number: "",
      businessAccount: false,
      catalogEnabled: false,
      catalogUrl: "",
      showOnApp: true,
    },
    email: {
      primary: "",
      support: "",
      sales: "",
      orders: "",
      complaints: "",
    },
    website: {
      url: "",
      catalogUrl: "",
      bookingUrl: "",
      ratesUrl: "",
      goldSchemeUrl: "",
    },
  });

  const [socialMedia, setSocialMedia] = useState([]);
  const [newSocial, setNewSocial] = useState({
    platform: "",
    url: "",
    handle: "",
  });

  useEffect(() => {
    if (contactInfo && Object.keys(contactInfo).length > 0) {
      setContactForm({
        phone: {
          primary: safeString(contactInfo.phone?.primary),
          secondary: safeString(contactInfo.phone?.secondary),
          sales: safeString(contactInfo.phone?.sales),
          tollfree: safeString(contactInfo.phone?.tollfree),
          landline: safeString(contactInfo.phone?.landline),
        },
        whatsapp: {
          number: safeString(contactInfo.whatsapp?.number),
          businessAccount: Boolean(contactInfo.whatsapp?.businessAccount),
          catalogEnabled: Boolean(contactInfo.whatsapp?.catalogEnabled),
          catalogUrl: safeString(contactInfo.whatsapp?.catalogUrl),
          showOnApp: contactInfo.whatsapp?.showOnApp !== false,
        },
        email: {
          primary: safeString(contactInfo.email?.primary),
          support: safeString(contactInfo.email?.support),
          sales: safeString(contactInfo.email?.sales),
          orders: safeString(contactInfo.email?.orders),
          complaints: safeString(contactInfo.email?.complaints),
        },
        website: {
          url: safeString(contactInfo.website?.url),
          catalogUrl: safeString(contactInfo.website?.catalogUrl),
          bookingUrl: safeString(contactInfo.website?.bookingUrl),
          ratesUrl: safeString(contactInfo.website?.ratesUrl),
          goldSchemeUrl: safeString(contactInfo.website?.goldSchemeUrl),
        },
      });
      setSocialMedia(
        Array.isArray(contactInfo.socialMedia) ? contactInfo.socialMedia : []
      );
    }
  }, [contactInfo]);

  const handleContactChange = (path, value) => {
    const keys = path.split(".");
    setContactForm((prev) => {
      const newForm = { ...prev };
      let current = newForm;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newForm;
    });
  };

  const handleSaveContact = async () => {
    await dispatch(
      updateContactInfoThunk({
        branchCode,
        contactInfo: contactForm,
      })
    );
    setIsEditingContact(false);
  };

  const handleAddSocial = () => {
    if (newSocial.platform && newSocial.url) {
      setSocialMedia((prev) => [...prev, { ...newSocial, isActive: true }]);
      setNewSocial({ platform: "", url: "", handle: "" });
    }
  };

  const handleRemoveSocial = (index) => {
    setSocialMedia((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveSocial = async () => {
    await dispatch(
      updateSocialMediaThunk({
        branchCode,
        socialMedia,
      })
    );
    setIsEditingSocial(false);
  };

  return (
    <div className="space-y-6">
      {/* Phone & WhatsApp */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium flex items-center">
            <PhoneIcon className="h-4 w-4 mr-2" />
            Contact Information
          </h4>
          {isRootAdmin && !isEditingContact && (
            <button
              onClick={() => setIsEditingContact(true)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <PencilIcon className="h-4 w-4" />
              Edit
            </button>
          )}
        </div>

        {isEditingContact ? (
          <div className="space-y-4">
            {/* Phone Numbers */}
            <div>
              <h5 className="text-sm font-medium text-gray-700 mb-2">
                Phone Numbers
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Primary Phone *
                  </label>
                  <input
                    type="tel"
                    value={contactForm.phone.primary}
                    onChange={(e) =>
                      handleContactChange("phone.primary", e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="9876543210"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Secondary Phone
                  </label>
                  <input
                    type="tel"
                    value={contactForm.phone.secondary}
                    onChange={(e) =>
                      handleContactChange("phone.secondary", e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="9876543211"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Sales Phone
                  </label>
                  <input
                    type="tel"
                    value={contactForm.phone.sales}
                    onChange={(e) =>
                      handleContactChange("phone.sales", e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="9876543212"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Toll-Free
                  </label>
                  <input
                    type="tel"
                    value={contactForm.phone.tollfree}
                    onChange={(e) =>
                      handleContactChange("phone.tollfree", e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="1800-xxx-xxxx"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Landline
                  </label>
                  <input
                    type="tel"
                    value={contactForm.phone.landline}
                    onChange={(e) =>
                      handleContactChange("phone.landline", e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="022-12345678"
                  />
                </div>
              </div>
            </div>

            {/* WhatsApp */}
            <div>
              <h5 className="text-sm font-medium text-gray-700 mb-2">
                WhatsApp
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    WhatsApp Number
                  </label>
                  <input
                    type="tel"
                    value={contactForm.whatsapp.number}
                    onChange={(e) =>
                      handleContactChange("whatsapp.number", e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="9876543210"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Catalog URL
                  </label>
                  <input
                    type="url"
                    value={contactForm.whatsapp.catalogUrl}
                    onChange={(e) =>
                      handleContactChange("whatsapp.catalogUrl", e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="https://wa.me/c/..."
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={contactForm.whatsapp.businessAccount}
                    onChange={(e) =>
                      handleContactChange(
                        "whatsapp.businessAccount",
                        e.target.checked
                      )
                    }
                    className="h-4 w-4 rounded"
                  />
                  <span className="text-sm">Business Account</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={contactForm.whatsapp.catalogEnabled}
                    onChange={(e) =>
                      handleContactChange(
                        "whatsapp.catalogEnabled",
                        e.target.checked
                      )
                    }
                    className="h-4 w-4 rounded"
                  />
                  <span className="text-sm">Catalog Enabled</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={contactForm.whatsapp.showOnApp}
                    onChange={(e) =>
                      handleContactChange(
                        "whatsapp.showOnApp",
                        e.target.checked
                      )
                    }
                    className="h-4 w-4 rounded"
                  />
                  <span className="text-sm">Show on App</span>
                </label>
              </div>
            </div>

            {/* Emails */}
            <div>
              <h5 className="text-sm font-medium text-gray-700 mb-2">
                Email Addresses
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Primary Email
                  </label>
                  <input
                    type="email"
                    value={contactForm.email.primary}
                    onChange={(e) =>
                      handleContactChange("email.primary", e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="info@store.com"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Support Email
                  </label>
                  <input
                    type="email"
                    value={contactForm.email.support}
                    onChange={(e) =>
                      handleContactChange("email.support", e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="support@store.com"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Sales Email
                  </label>
                  <input
                    type="email"
                    value={contactForm.email.sales}
                    onChange={(e) =>
                      handleContactChange("email.sales", e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="sales@store.com"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Orders Email
                  </label>
                  <input
                    type="email"
                    value={contactForm.email.orders}
                    onChange={(e) =>
                      handleContactChange("email.orders", e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="orders@store.com"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Complaints Email
                  </label>
                  <input
                    type="email"
                    value={contactForm.email.complaints}
                    onChange={(e) =>
                      handleContactChange("email.complaints", e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="complaints@store.com"
                  />
                </div>
              </div>
            </div>

            {/* Website URLs */}
            <div>
              <h5 className="text-sm font-medium text-gray-700 mb-2">
                Website URLs
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Main Website
                  </label>
                  <input
                    type="url"
                    value={contactForm.website.url}
                    onChange={(e) =>
                      handleContactChange("website.url", e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="https://www.store.com"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Catalog URL
                  </label>
                  <input
                    type="url"
                    value={contactForm.website.catalogUrl}
                    onChange={(e) =>
                      handleContactChange("website.catalogUrl", e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="https://www.store.com/catalog"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Booking URL
                  </label>
                  <input
                    type="url"
                    value={contactForm.website.bookingUrl}
                    onChange={(e) =>
                      handleContactChange("website.bookingUrl", e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="https://www.store.com/book"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Live Rates URL
                  </label>
                  <input
                    type="url"
                    value={contactForm.website.ratesUrl}
                    onChange={(e) =>
                      handleContactChange("website.ratesUrl", e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="https://www.store.com/rates"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Gold Scheme URL
                  </label>
                  <input
                    type="url"
                    value={contactForm.website.goldSchemeUrl}
                    onChange={(e) =>
                      handleContactChange(
                        "website.goldSchemeUrl",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="https://www.store.com/gold-scheme"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={handleSaveContact}
                disabled={loadingSection === "contactInfo"}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
              >
                <CheckCircleIcon className="h-4 w-4" />
                Save Contact Info
              </button>
              <button
                onClick={() => setIsEditingContact(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div>
                <span className="text-sm text-gray-600">Primary Phone:</span>
                <p className="font-medium">
                  {safeString(contactInfo?.phone?.primary) || "Not set"}
                </p>
              </div>
              {safeString(contactInfo?.phone?.secondary) && (
                <div>
                  <span className="text-sm text-gray-600">
                    Secondary Phone:
                  </span>
                  <p className="font-medium">
                    {safeString(contactInfo.phone.secondary)}
                  </p>
                </div>
              )}
              {safeString(contactInfo?.phone?.sales) && (
                <div>
                  <span className="text-sm text-gray-600">Sales Phone:</span>
                  <p className="font-medium">
                    {safeString(contactInfo.phone.sales)}
                  </p>
                </div>
              )}
              {safeString(contactInfo?.whatsapp?.number) && (
                <div>
                  <span className="text-sm text-gray-600">WhatsApp:</span>
                  <p className="font-medium">
                    {safeString(contactInfo.whatsapp.number)}
                    {contactInfo.whatsapp?.businessAccount && (
                      <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                        Business
                      </span>
                    )}
                  </p>
                </div>
              )}
            </div>
            <div className="space-y-3">
              {safeString(contactInfo?.email?.primary) && (
                <div>
                  <span className="text-sm text-gray-600">Email:</span>
                  <p className="font-medium">
                    {safeString(contactInfo.email.primary)}
                  </p>
                </div>
              )}
              {safeString(contactInfo?.email?.support) && (
                <div>
                  <span className="text-sm text-gray-600">Support Email:</span>
                  <p className="font-medium">
                    {safeString(contactInfo.email.support)}
                  </p>
                </div>
              )}
              {safeString(contactInfo?.website?.url) && (
                <div>
                  <span className="text-sm text-gray-600">Website:</span>
                  <p className="font-medium">
                    <a
                      href={safeString(contactInfo.website.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {safeString(contactInfo.website.url)}
                    </a>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Social Media */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium flex items-center">
            <GlobeAltIcon className="h-4 w-4 mr-2" />
            Social Media Links
          </h4>
          {isRootAdmin && !isEditingSocial && (
            <button
              onClick={() => setIsEditingSocial(true)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <PencilIcon className="h-4 w-4" />
              Edit
            </button>
          )}
        </div>

        {isEditingSocial ? (
          <div className="space-y-4">
            {/* Current Social Links */}
            {socialMedia.length > 0 && (
              <div className="space-y-2">
                {socialMedia.map((social, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 bg-white p-2 rounded border"
                  >
                    <span className="px-2 py-1 bg-gray-100 rounded text-sm font-medium">
                      {SOCIAL_PLATFORMS.find((p) => p._id === social.platform)
                        ?.name ||
                        safeString(social.platform) ||
                        "Unknown"}
                    </span>
                    <span className="flex-1 text-sm truncate">
                      {safeString(social.url)}
                    </span>
                    <button
                      onClick={() => handleRemoveSocial(index)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add New */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <Dropdown
                options={SOCIAL_PLATFORMS}
                selected={SOCIAL_PLATFORMS.find(
                  (p) => p._id === newSocial.platform
                )}
                onChange={(selected) =>
                  setNewSocial((prev) => ({
                    ...prev,
                    platform: selected?._id || "",
                  }))
                }
                placeholder="Platform"
              />
              <input
                type="text"
                value={newSocial.handle}
                onChange={(e) =>
                  setNewSocial((prev) => ({ ...prev, handle: e.target.value }))
                }
                className="px-3 py-2 border rounded-lg"
                placeholder="@handle"
              />
              <input
                type="url"
                value={newSocial.url}
                onChange={(e) =>
                  setNewSocial((prev) => ({ ...prev, url: e.target.value }))
                }
                className="px-3 py-2 border rounded-lg"
                placeholder="https://..."
              />
              <button
                onClick={handleAddSocial}
                disabled={!newSocial.platform || !newSocial.url}
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <PlusIcon className="h-4 w-4" />
                Add
              </button>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={handleSaveSocial}
                disabled={loadingSection === "socialMedia"}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
              >
                <CheckCircleIcon className="h-4 w-4" />
                Save Social Links
              </button>
              <button
                onClick={() => setIsEditingSocial(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-3">
            {Array.isArray(contactInfo?.socialMedia) &&
            contactInfo.socialMedia.length > 0 ? (
              contactInfo.socialMedia
                .filter((s) => s && s.isActive)
                .map((social, index) => (
                  <a
                    key={index}
                    href={safeString(social.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 bg-white border rounded-lg hover:bg-gray-50 flex items-center gap-2"
                  >
                    <span className="font-medium text-sm">
                      {SOCIAL_PLATFORMS.find((p) => p._id === social.platform)
                        ?.name || safeString(social.platform)}
                    </span>
                    {safeString(social.handle) && (
                      <span className="text-sm text-gray-500">
                        {safeString(social.handle)}
                      </span>
                    )}
                  </a>
                ))
            ) : (
              <p className="text-gray-500 text-sm">
                No social media links added
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactInfoSection;
